import CloseOutlined from '@ant-design/icons/CloseOutlined'
import { Link } from '@tanstack/react-router'
import { Button, Drawer, Space, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { useInspectionReportDownload } from 'features/inbox/hooks/useInspectionReportDownload'
import { EnrichedGeckoInspection } from 'features/inbox/types/types'
import { entryToIndication, IndicationItem } from 'features/indicationTriage'
import { useRolodexEntries } from 'features/rolodex'
import { Download, ExternalLink } from 'lucide-react'
import React, { memo, useCallback, useMemo } from 'react'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataset,
  GeckoEntry,
  GeckoJob,
  GeckoUnit,
  RolodexEntryType,
} from 'types'

import { InspectionDetailsPage } from './InspectionDetailsPage'

interface InspectionDetailsDrawerProps {
  open: boolean
  onClose: () => void
  inspection?: EnrichedGeckoInspection
  job?: GeckoJob
  component?: GeckoComponent
  unit?: GeckoUnit
  componentDataset?: GeckoComponentDataset
  assetModel?: GeckoAssetModel
}

const InspectionDetailsDrawerComponent: React.FC<
  InspectionDetailsDrawerProps
> = ({
  open,
  onClose,
  inspection,
  job,
  component,
  unit,
  componentDataset,
  assetModel,
}) => {
  const {
    reportCount,
    hasSignedUrls,
    isLoading: isReportLoading,
    isDownloading,
    handleDownloadReport,
  } = useInspectionReportDownload({
    unitId: unit?.id,
    inspectionId: inspection?.id,
  })

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const title = useMemo(() => {
    return (
      dayjs(inspection?.data.date).format('MMM D YYYY') +
      ': ' +
      unit?.data.name +
      ' - ' +
      component?.data.name
    )
  }, [component?.data.name, inspection?.data.date, unit?.data.name])

  const indicationsQuery = useMemo(() => {
    if (!inspection?.id) return undefined
    return {
      type: RolodexEntryType.INDICATION,
      tags: { inspection: inspection.id },
      limit: 10000,
    }
  }, [inspection?.id])

  const { entries: indicationEntries, isLoading: areIndicationsLoading } =
    useRolodexEntries(indicationsQuery)

  const indications: readonly IndicationItem[] = useMemo(() => {
    return indicationEntries.map((entry) => entryToIndication(entry))
  }, [indicationEntries])

  const indicationIds = useMemo(
    () => indications.map((ind) => ind.id),
    [indications]
  )

  const ffsReportsQuery = useMemo(() => {
    if (indicationIds.length === 0) return undefined
    return {
      type: RolodexEntryType.FFS_LTA_REPORT,
      tags: { indication: indicationIds },
      limit: 10000,
    }
  }, [indicationIds])

  const { entries: ffsReportEntries } = useRolodexEntries(ffsReportsQuery)

  const ffsReports: readonly GeckoEntry[] = useMemo(() => {
    return ffsReportEntries
  }, [ffsReportEntries])

  const extra = useMemo(() => {
    if (!component || !inspection) return null

    const isDisabled = reportCount === 0 || (!hasSignedUrls && !isReportLoading)
    const tooltipText =
      reportCount === 0
        ? 'No report available'
        : reportCount > 1
          ? `Download ${reportCount} reports`
          : 'Download report'

    return (
      <Space>
        <Tooltip title={tooltipText}>
          <Button
            icon={<Download size={14} />}
            loading={isDownloading || isReportLoading}
            onClick={handleDownloadReport}
            disabled={isDisabled}>
            Download Report{reportCount > 1 ? 's' : ''}
          </Button>
        </Tooltip>
        <Link
          to='/details/$entryID'
          params={{ entryID: component.id }}
          search={{ inspectionId: inspection.id }}
          target='_blank'
          rel='noopener noreferrer'>
          <Button type='primary' icon={<ExternalLink size={14} />}>
            Open Visualization
          </Button>
        </Link>
      </Space>
    )
  }, [
    component,
    inspection,
    reportCount,
    hasSignedUrls,
    isReportLoading,
    isDownloading,
    handleDownloadReport,
  ])

  return (
    <Drawer
      title={title}
      placement='right'
      size={1100}
      onClose={handleClose}
      open={open}
      maskClosable={true}
      destroyOnClose={true}
      closeIcon={<CloseOutlined />}
      extra={extra}>
      <InspectionDetailsPage
        inspection={inspection}
        job={job}
        component={component}
        unit={unit}
        componentDataset={componentDataset}
        assetModel={assetModel}
        indications={indications}
        areIndicationsLoading={areIndicationsLoading}
        ffsReports={ffsReports}
      />
    </Drawer>
  )
}

export const InspectionDetailsDrawer = memo(InspectionDetailsDrawerComponent)
